import {useEffect} from 'react'

type UseSyncCookieToCustomerProfileOptions<TFormattedCookie, TCustomerProfileValue, TCookieInput> =
    {
        isRegistered: boolean
        isCustomerDataLoading: boolean
        shouldQueryCustomerData?: boolean
        customerId: string | null
        cookieValues?: TCookieInput | null
        customerProfileValue: TCustomerProfileValue | null
        formatCookie: (values: TCookieInput) => TFormattedCookie
        shouldUpdateCookie: (
            cookie: TFormattedCookie,
            customerProfileValue: TCustomerProfileValue
        ) => boolean
        buildCustomerPayload?: (
            formattedCookie: TFormattedCookie,
            customerProfileValue: TCustomerProfileValue
        ) => unknown
        mutateCustomerData: (customerId: string, payload: any) => void
    }

export function useSyncCookieToCustomerProfile<
    TFormattedCookie,
    TCustomerProfileValue,
    TCookieInput
>({
    isRegistered,
    isCustomerDataLoading,
    shouldQueryCustomerData = false,
    customerId,
    cookieValues,
    customerProfileValue,
    formatCookie,
    shouldUpdateCookie,
    buildCustomerPayload,
    mutateCustomerData
}: UseSyncCookieToCustomerProfileOptions<TFormattedCookie, TCustomerProfileValue, TCookieInput>) {
    useEffect(() => {
        if (
            !cookieValues ||
            !isRegistered ||
            !customerId ||
            (isCustomerDataLoading && shouldQueryCustomerData)
        )
            return
        const formattedCookie = formatCookie(cookieValues)
        const shouldUpdate = customerProfileValue
            ? shouldUpdateCookie(formattedCookie, customerProfileValue)
            : true

        if (customerProfileValue === null || !shouldUpdate) return

        const payload = buildCustomerPayload
            ? buildCustomerPayload(formattedCookie, customerProfileValue)
            : formattedCookie

        mutateCustomerData(customerId, payload)
        // Don't add mutateCustomerData to the dependency array
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isRegistered,
        isCustomerDataLoading,
        shouldQueryCustomerData,
        customerId,
        cookieValues,
        customerProfileValue,
        shouldUpdateCookie,
        formatCookie,
        buildCustomerPayload
    ])
}
